import { Controller, type UseFormReturn } from 'react-hook-form';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import type { AdminClientPassbook } from '@/shared/types/client';
import { Field, PARTNER_OPTIONS, NONE_VALUE, type FormValues } from './client-clone-dialog';

interface ClientCloneAdvancedFieldsProps {
  form: UseFormReturn<FormValues>;
  /** Passbook do client de origem — só pra decidir o que exibir (licença,
   * máximo de inputs); os valores em si já vêm pré-preenchidos no form. */
  sourcePassbook: AdminClientPassbook | undefined;
}

const PARTNER_LABELS: Record<string, string> = {
  webautomacao: 'webautomacao',
  colibri: 'colibri',
  goomer: 'goomer',
  totvs: 'totvs',
};

/**
 * Campos avançados do programa, editáveis no 1º step da clonagem. Todos já
 * vêm pré-preenchidos com o valor do client de origem (ver `defaultsFor`
 * em `client-clone-dialog.tsx`) — o operador só mexe no que precisa mudar.
 */
export function ClientCloneAdvancedFields({ form, sourcePassbook }: ClientCloneAdvancedFieldsProps) {
  const errors = form.formState.errors;
  const partnerName = form.watch('partnerName');
  const showLicenca = sourcePassbook?.partnerName === 'webautomacao';
  const showStampFields = !sourcePassbook?.hasPoints && !sourcePassbook?.hasCashback;

  return (
    <div className="space-y-4">
      <p className="text-xs font-medium text-muted-foreground">Configuração do programa</p>

      {showLicenca ? (
        <Field label="Licença (webautomacao)" error={errors.licenca?.message}>
          <Input placeholder="Código da licença na webautomacao" {...form.register('licenca')} />
        </Field>
      ) : null}

      {showStampFields ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Máximo de selos (maxInputValidation)" error={errors.maxInputValidation?.message}>
            <Input type="number" placeholder="10" {...form.register('maxInputValidation')} />
          </Field>
          <Field label="Saldo inicial (defaultBalance)" error={errors.defaultBalance?.message}>
            <Input type="number" placeholder="0" {...form.register('defaultBalance')} />
          </Field>
        </div>
      ) : null}

      {showStampFields ? (
        <SwitchField form={form} name="hasExpireStamps" label="Selos expiram (hasExpireStamps)" />
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Dias para expirar" error={errors.expireDays?.message}>
          <Input type="number" placeholder="1000" {...form.register('expireDays')} />
        </Field>
        <Field label="Tipo de resgate">
          <Controller
            control={form.control}
            name="redeemType"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Nenhum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>Nenhum</SelectItem>
                  <SelectItem value="discount">discount</SelectItem>
                  <SelectItem value="product">product</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </Field>
      </div>

      <Field label="Integração (partnerName)">
        <div className="grid gap-2 sm:grid-cols-2">
          <Controller
            control={form.control}
            name="partnerName"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Nenhuma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_VALUE}>Nenhuma</SelectItem>
                  {PARTNER_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {PARTNER_LABELS[option]}
                    </SelectItem>
                  ))}
                  <SelectItem value="outro">Outro…</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {partnerName === 'outro' ? (
            <Input placeholder="Nome da integração" {...form.register('partnerNameOther')} />
          ) : null}
        </div>
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <SwitchField form={form} name="partner" label="Tem integração (partner)" />
        <SwitchField form={form} name="hasMultiStore" label="Múltiplas lojas (hasMultiStore)" />
        {/* No modo "selos" quem manda é hasExpireStamps (já exibido acima) —
            hasExpire não se aplica, não faz sentido mostrar os dois. */}
        {!showStampFields ? (
          <SwitchField form={form} name="hasExpire" label="Pontos expiram (hasExpire)" />
        ) : null}
        <SwitchField form={form} name="hasGWallet" label="Google Wallet (hasGWallet)" />
        <SwitchField form={form} name="hasFixeGWallet" label="Fixe Google Wallet (hasFixeGWallet)" />
      </div>
    </div>
  );
}

function SwitchField({
  form,
  name,
  label,
}: {
  form: UseFormReturn<FormValues>;
  name: 'partner' | 'hasMultiStore' | 'hasExpire' | 'hasExpireStamps' | 'hasGWallet' | 'hasFixeGWallet';
  label: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-[hsl(var(--glass-bg)/0.4)] px-4 py-3">
      <Label htmlFor={name} className="text-sm font-normal">
        {label}
      </Label>
      <Controller
        control={form.control}
        name={name}
        render={({ field }) => <Switch id={name} checked={field.value} onCheckedChange={field.onChange} />}
      />
    </div>
  );
}
